import React, { useEffect, useState } from "react";
import { Alert, Empty, Input, Modal, Table, Tag, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useOrderSearch } from "../../../../data/hooks";
import { MIN_SEARCH_LENGTH, SEARCH_ORDER_NUMBER } from "../../../../functions/orderSearch";
import { formatPrice, getPaymentStatus, getPaymentStatusColor } from "../../../../functions/formatters";
import { CURRENCY, DELIVERY_OPTIONS } from "../../../../constants/DeliveryConstants";
import { parseDate } from "../../../../functions/workDay";

/**
 * The order search on the orders page (HVC-90): one bar that finds an order of either shop by order
 * number, email, name, company, phone or postcode, for a customer who calls or mails without their
 * order number. Which queries run is decided in functions/orderSearch.js, only on Enter or the button.
 *
 * One order found on its order number opens the order drawer straight away, as the old order number
 * search did; everything else opens a list of what was found. The list sits just below the drawer
 * (zIndex 999 against 1000), so a row opens the drawer on top and closing it lands back on the list.
 * `drawerOrder` is the order the drawer shows: once it is edited there, its row shows the new data.
 */

const orderKey = (order) => `${order.shopSource}|${order.id}`;

/** `delivery.date` (DD-MM-YYYY) as "vr 9 okt. 2026"; parsed by hand, like functions/workDay.js does. */
const formatDeliveryDate = (value) => {
  const date = parseDate(value);
  if (!date) return value || "-";
  return dayjs(new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())).format("dd D MMM YYYY");
};

const secondary = { fontSize: 12 };

const OrderSearch = ({ onOpenOrder, drawerOrder }) => {
  const { t } = useTranslation();
  const { search, searching } = useOrderSearch();
  const [input, setInput] = useState("");
  // What the list shows: `{ query, orders, total, incomplete, failedShops }`, or null while it is closed
  const [result, setResult] = useState(null);

  // Closing the drawer after an edit must not land on the old data
  useEffect(() => {
    if (!drawerOrder) return;
    setResult((current) =>
      current?.orders.some((item) => orderKey(item) === orderKey(drawerOrder))
        ? {
            ...current,
            orders: current.orders.map((item) => (orderKey(item) === orderKey(drawerOrder) ? drawerOrder : item)),
          }
        : current,
    );
  }, [drawerOrder]);

  // Below the list: how many, and whether they are all of them
  const footerText = ({ orders, total, incomplete }) => {
    if (incomplete) return t("order_search.shown", { count: orders.length });
    if (total > orders.length) return t("order_search.newest_of", { shown: orders.length, total });
    return t("order_search.count", { count: orders.length });
  };

  const run = async (value) => {
    const query = (value ?? input).trim();
    if (query.length < MIN_SEARCH_LENGTH) {
      message.warning(t("order_search.too_short", { count: MIN_SEARCH_LENGTH }));
      return;
    }
    try {
      const found = await search(query);
      if (!found.searchable) {
        message.warning(t("order_search.nothing_to_search"));
        return;
      }
      if (found.failedShops.length === 2) {
        message.error(t("order_search.failed"));
        return;
      }
      if (found.kind === SEARCH_ORDER_NUMBER && found.orders.length === 1 && found.failedShops.length === 0) {
        onOpenOrder(found.orders[0]);
        return;
      }
      setResult({ query, ...found });
    } catch (err) {
      console.error("[OrderSearch] ❌ Search failed:", err);
      message.error(t("order_search.failed"));
    }
  };

  const columns = [
    {
      title: t("customer.customer"),
      dataIndex: "customer",
      render: (customer) => (
        <div>
          <div>
            {customer?.firstName} {customer?.lastName}
          </div>
          {!!customer?.address?.delivery?.companyName && (
            <Typography.Text type="secondary" style={{ ...secondary, display: "block" }}>
              {customer.address.delivery.companyName}
            </Typography.Text>
          )}
          <Typography.Text type="secondary" style={{ ...secondary, display: "block" }}>
            {customer?.email}
          </Typography.Text>
        </div>
      ),
    },
    {
      // Ordered on, and delivered or picked up on: the delivery type is the label of the second date
      title: t("date"),
      key: "dates",
      render: (_, order) => (
        // The label column has a minimum width, so the dates line up from row to row as well
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(64px, max-content) auto",
            columnGap: 8,
            alignItems: "baseline",
            whiteSpace: "nowrap",
          }}
        >
          <Typography.Text type="secondary" style={secondary}>
            {t("order_search.ordered")}
          </Typography.Text>
          <span>{order.createdAt ? dayjs.unix(order.createdAt).format("D MMM YYYY HH:mm") : "-"}</span>
          <Typography.Text type="secondary" style={secondary}>
            {order.delivery?.type === DELIVERY_OPTIONS.PICKUP ? t("pickup_short") : t("shipping")}
          </Typography.Text>
          <span>{formatDeliveryDate(order.delivery?.date)}</span>
        </div>
      ),
    },
    {
      title: t("shop"),
      dataIndex: "shopSource",
      render: (shopSource) => <Tag color={shopSource === "VCE" ? "green" : "purple"}>{shopSource}</Tag>,
    },
    {
      title: t("order.order_id"),
      dataIndex: "orderId",
      render: (orderId) => <span style={{ whiteSpace: "nowrap" }}>{orderId}</span>,
    },
    {
      title: t("status"),
      dataIndex: "orderStatus",
      render: (status) => <Tag color={getPaymentStatusColor(status)}>{getPaymentStatus(status)}</Tag>,
    },
    {
      title: t("total"),
      dataIndex: "totalPrice",
      render: (totalPrice) => <span className="font-weight-semibold">{CURRENCY + formatPrice(totalPrice)}</span>,
    },
  ];

  return (
    <>
      <Input.Search
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={t("order_search.placeholder")}
        enterButton={t("search")}
        loading={searching}
        onSearch={run}
        allowClear
        style={{ width: 420 }}
      />
      <Modal
        open={!!result}
        onCancel={() => setResult(null)}
        footer={null}
        width={1200}
        zIndex={999}
        title={t("order_search.title", { query: result?.query })}
      >
        {!!result && (
          <>
            {result.failedShops.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={t("order_search.shop_failed", { shops: result.failedShops.join(", ") })}
              />
            )}
            {result.incomplete && (
              <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t("order_search.incomplete")} />
            )}
            {result.orders.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <>
                    <div>{t("order_search.none", { query: result.query })}</div>
                    <Typography.Text type="secondary">{t("order_search.hint")}</Typography.Text>
                  </>
                }
              />
            ) : (
              <>
                <Table
                  rowKey={orderKey}
                  columns={columns}
                  dataSource={result.orders}
                  pagination={false}
                  size="small"
                  scroll={{ x: "max-content", y: 520 }}
                  onRow={(order) => ({ onClick: () => onOpenOrder(order), style: { cursor: "pointer" } })}
                />
                <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                  {footerText(result)}
                </Typography.Paragraph>
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default OrderSearch;
